import {
  exportTableToCSV,
  type SerializableColumnConfig,
} from "~/ui-rtk/utils/csv-export"
import { describeRecentChange } from "./budget-utils"
import type { RecommendationRow } from "./types"

export const REVIEW_CSV_COLUMNS: SerializableColumnConfig[] = [
  { key: "entityName", header: "Entity Name", type: "string" },
  { key: "entityType", header: "Entity Type", type: "string" },
  { key: "campaignId", header: "Campaign ID", type: "string" },
  { key: "adsetId", header: "Adset ID", type: "string" },
  { key: "channel", header: "Channel", type: "string" },
  { key: "initiativeType", header: "Initiative Type", type: "string" },
  { key: "avgDailySpend", header: "Avg Daily Spend", type: "number" },
  { key: "currentDailyBudget", header: "Current Daily Budget", type: "number" },
  { key: "newDailyBudget", header: "New Daily Budget", type: "number" },
  { key: "pctChange", header: "Change %", type: "number" },
  { key: "action", header: "Action", type: "string" },
  { key: "recentChanges", header: "Recent Changes", type: "string" },
]

const round2 = (n: number) => Math.round(n * 100) / 100

function rowBase(
  row: RecommendationRow,
  formatInitiativeType: (raw: string) => string,
) {
  return {
    entityName: row.entityName,
    entityType: row.entityType,
    campaignId: row.campaignId,
    adsetId: row.entityType === "adset" ? row.entityId : "",
    channel: row.channel,
    initiativeType: formatInitiativeType(row.initiativeType),
    avgDailySpend: round2(row.avgDailySpend),
    currentDailyBudget: round2(row.currentDailyBudget),
    recentChanges: (row.recentChanges ?? [])
      .map(describeRecentChange)
      .join("; "),
  }
}

/**
 * One CSV row per row under review. `rows` are the rows being changed;
 * `unchangedRows` (active rows left at their current budget) follow them
 * with a "No change" action, so the export is the whole pool the summary
 * totals, not just the subset being written.
 */
export function buildReviewCsvRows(
  rows: RecommendationRow[],
  getBudget: (row: RecommendationRow) => number,
  formatInitiativeType: (raw: string) => string,
  unchangedRows: RecommendationRow[] = [],
): Record<string, unknown>[] {
  const changing = rows.map(row => {
    const newBudget = getBudget(row)
    const pctChange =
      row.avgDailySpend > 0
        ? ((newBudget - row.avgDailySpend) / row.avgDailySpend) * 100
        : 0
    return {
      ...rowBase(row, formatInitiativeType),
      newDailyBudget: row.shouldPause ? "" : round2(newBudget),
      pctChange: row.shouldPause ? "" : round2(pctChange),
      action: row.shouldPause ? "Pause" : "Update Budget",
    }
  })
  const unchanged = unchangedRows.map(row => ({
    ...rowBase(row, formatInitiativeType),
    newDailyBudget: round2(row.currentDailyBudget),
    pctChange: 0,
    action: "No change",
  }))
  return [...changing, ...unchanged]
}

export function exportReviewChangesCsv(
  rows: RecommendationRow[],
  getBudget: (row: RecommendationRow) => number,
  formatInitiativeType: (raw: string) => string,
  unchangedRows: RecommendationRow[] = [],
): void {
  const csvRows = buildReviewCsvRows(
    rows,
    getBudget,
    formatInitiativeType,
    unchangedRows,
  )
  const date = new Date().toISOString().slice(0, 10)
  exportTableToCSV(csvRows, REVIEW_CSV_COLUMNS, {
    filename: `budget-changes-${date}.csv`,
  })
}
